import React from 'react';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { Verification } from '../../../types/verification';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  verification: Verification | null;
  action: 'approve' | 'reject' | 'delete';
  rejectionReason?: string;
  onClose: () => void;
  onConfirm: () => void;
  onRejectionReasonChange: (reason: string) => void;
};

export const ConfirmationDialog = ({
  isOpen,
  verification,
  action,
  rejectionReason,
  onClose,
  onConfirm,
  onRejectionReasonChange
}: Props) => {
  const { translations } = useLanguage();

  const getTitle = () => {
    switch (action) {
      case 'approve':
        return translations?.verificationApproval?.dialog?.approveTitle || 'Approve Verification';
      case 'reject':
        return translations?.verificationApproval?.dialog?.rejectTitle || 'Reject Verification';
      case 'delete':
        return translations?.verificationApproval?.dialog?.deleteTitle || 'Delete Verification';
      default:
        return '';
    }
  };

  const getMessage = () => {
    switch (action) {
      case 'approve':
        return translations?.verificationApproval?.dialog?.approveMessage || 
               'Are you sure you want to approve this verification?';
      case 'reject':
        return (
          <div className="space-y-4">
            <p>
              {translations?.verificationApproval?.dialog?.rejectMessage ||
                'Are you sure you want to reject this verification?'}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.verificationApproval?.dialog?.rejectionReasonLabel || 
                 'Reason for rejection'}
              </label>
              <textarea
                value={rejectionReason || ''}
                onChange={(e) => onRejectionReasonChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500"
                rows={3}
                required
              />
            </div>
          </div>
        );
      case 'delete':
        return translations?.verificationApproval?.dialog?.deleteMessage || 
               `Are you sure you want to delete this verification for ${verification?.userInfo.fullName}? This action cannot be undone.`;
      default:
        return '';
    }
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={getTitle()}
      message={getMessage()}
      type={action === 'approve' ? 'info' : 'danger'}
      confirmText={
        action === 'approve'
          ? translations?.verificationApproval?.buttons?.approve || 'Approve'
          : action === 'reject'
          ? translations?.verificationApproval?.buttons?.reject || 'Reject'
          : translations?.verificationApproval?.buttons?.delete || 'Delete'
      }
      cancelText={translations?.verificationApproval?.buttons?.cancel || 'Cancel'}
    />
  );
};